import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeFactory} from 'common/widgets/date-range-picker/models/date-range-factory';
import {RelativeDateRange} from 'common/widgets/date-range-picker/models/relative-date-range';
import {BaseDate, DateRangeType, DisplayContext} from 'common/widgets/date-range-picker/models/types';

import {ChildMenu} from '../child-menu';
import template from './index.jade';
import {ComparisonAttrs, ComparisonDate, ComparisonState} from './types';
import {getComparisonType, getPresetMatch, getRelativeDateFromComparisonDate} from './utils';

export class TimeComparisonMenu extends ChildMenu<ComparisonState, ComparisonAttrs> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      selected: {type: `json`, description: `interface: ComparisonDate`},
      'disabled-message': {type: `string`, default: null},
      'date-range': {type: `json`, description: `interface: DateRange`},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }
  get config() {
    return {
      template,
      defaultState: {
        stagedComparisonDate: null,
        editingCustomComparison: false,
        stagedComparisonType: DateRangeType.StartsOn,
      },
      helpers: {
        ...super.config.helpers,
        onPaneBackClick: () => {
          this.update({editingCustomComparison: false});
        },
        getTimeComparisonOptions: () => {
          const maxDataHistoryDays = this.attr(`max-data-history-days`);
          const matchedPreset = getPresetMatch(this.attr(`selected`));
          const presetOptions = RelativeDateRange.getTimeComparisonPresetOptions()
            .filter((option) => {
              const exceedsMaxHistory =
                maxDataHistoryDays &&
                !new RelativeDateRange({type: option.value.type, window: option.value.offset}).isWithinMaxHistoryLimit(
                  maxDataHistoryDays,
                );
              return !exceedsMaxHistory;
            })
            .map((option) => {
              return {
                isSelected: matchedPreset?.label === option.label,
                label: option.label,
                value: option.value.offset,
              };
            });

          const customItem = {
            hasCaret: true,
            label: `Custom date range...`,
            value: `custom`,
          };

          return [
            {
              items: [...presetOptions, customItem],
            },
          ];
        },
        onPresetTimeComparisonClick: (ev) => {
          const {value} = ev.detail.item;

          if (value === `custom`) {
            this.update({editingCustomComparison: true});
          } else {
            this.onComparisonDateRangeSelected({offset: value});
          }
        },
        getDateRangePickerOptions: () => {
          const type = this.comparisonType;
          const dateRange = DateRangeFactory.createDateRange({type}, {displayContext: DisplayContext.RelativeRange});
          if (this.state.stagedComparisonDate) {
            dateRange.from = this.state.stagedComparisonDate.from ?? this.state.stagedComparisonDate.to;
          } else if (this.attr(`selected`)) {
            const {to, from} = this.attr(`selected`);
            if (from) {
              dateRange.from = from;
            } else if (to) {
              dateRange.from = to;
            }
          }

          if (dateRange.from && DateRange.isRelativeDate(dateRange.from)) {
            dateRange.rolling = true;
          }
          return {
            'display-context': DisplayContext.RelativeRange,
            'date-range': JSON.stringify(dateRange),
            'max-date': this.getMaxCalendarDate(),
            'max-history-days': this.attr(`max-data-history-days`),
          };
        },
        changedDateRange: (ev) => {
          const {from: date, type: stagedComparisonType} = ev.detail.dateRange;
          let stagedComparisonDate = this.state.stagedComparisonDate;
          if (date) {
            stagedComparisonDate = stagedComparisonType === DateRangeType.StartsOn ? {from: date} : {to: date};
          }
          this.update({
            stagedComparisonDate,
            stagedComparisonType,
          });
        },
        applyDateRange: () => {
          if (this.state.stagedComparisonDate) {
            this.onComparisonDateRangeSelected(this.state.stagedComparisonDate);
          }
        },
      },
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected`) {
      this.update({
        stagedComparisonDate: this.attr(`selected`),
        stagedComparisonType: getComparisonType(this.attr(`selected`)),
      });
    }
  }

  onComparisonDateRangeSelected(comparisonDate: ComparisonDate) {
    this.dispatchEvent(new CustomEvent(`select`, {detail: comparisonDate}));
    this.trackSelection(this.getTrackingProps(comparisonDate));
  }

  handleLabelClick() {
    // when the menu is opened, show the custom date range picker only if a custom range is selected
    // (even if a custom date has been staged previously)
    this.update({editingCustomComparison: this.attr(`selected`) && !getPresetMatch(this.attr(`selected`))});
  }

  selectedLabel(): string {
    if (!this.attr(`selected`)) {
      return `Compare to Past`;
    }

    if (this.attr(`selected`).offset) {
      return `Compare to previous ${this.attr(`selected`).offset.unit}`;
    } else {
      const {to, from} = this.attr(`selected`);
      const rolling = DateRange.isRelativeDate(from) || DateRange.isRelativeDate(to);
      return `Compare to date range ${from ? `starting` : `ending`} on ${
        DateRange.convertBaseDateToCalendarDate(to) ?? DateRange.convertBaseDateToCalendarDate(from)
      }${rolling ? ` (rolling)` : ``}`;
    }
  }

  isContentSearchable() {
    return false;
  }

  getMaxCalendarDate(): BaseDate {
    const dates = this.dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`));
    let maxDate: BaseDate = dates.to;
    if (this.comparisonType === DateRangeType.StartsOn) {
      maxDate = dates.from;
    }

    return maxDate;
  }

  get dateRange(): DateRange {
    return new DateRange(this.attr(`date-range`));
  }

  get comparisonType(): DateRangeType {
    if (this.state.stagedComparisonType) {
      return this.state.stagedComparisonType;
    }
    return getComparisonType(this.attr(`selected`));
  }

  getTrackingProps(comparisonDate: ComparisonDate) {
    const presetMatch = getPresetMatch(comparisonDate);
    const type = presetMatch ? presetMatch.value.type : getComparisonType(comparisonDate);
    const custom = !presetMatch;
    const customRolling =
      DateRange.isRelativeDate(comparisonDate.from) || DateRange.isRelativeDate(comparisonDate.to) || false;
    const relativeDate = getRelativeDateFromComparisonDate(comparisonDate);
    return {
      eventName: `Select comparison date range`,
      props: {
        type,
        'window unit': relativeDate.unit,
        'window value': relativeDate.value,
        custom,
        'custom rolling': customRolling,
      },
    };
  }
}

export const TimeComparisonMenuEl = defineAndMakeJsxFactory(`mp-time-comparison-menu`, TimeComparisonMenu);
